/**
 * localStorage mini-snapshot (<2 KB), written on every save. If iOS evicts
 * IndexedDB (7-day rule), the world is reconstructed from this: same seed,
 * same population and aggregate needs — the player loses fine detail,
 * never the relationship.
 */
import { SAVE_VERSION, type SaveV1 } from './schema';
import { LIFE } from '../sim/store';

const KEY = 'tg.mini';

interface MiniSave {
  v: number;
  seed: string;
  createdAt: number;
  savedAt: number;
  simTick: number;
  simTimeSec: number;
  pop: number;
  dormant: number;
  needAvg: [number, number, number, number]; // hunger, clean, fun, rest
  onboarding: SaveV1['onboarding'];
}

export function writeMini(save: SaveV1): void {
  try {
    let h = 0;
    let c = 0;
    let f = 0;
    let r = 0;
    let dormant = 0;
    for (const cr of save.creatures) {
      h += cr.hunger;
      c += cr.clean;
      f += cr.fun;
      r += cr.rest;
      if (cr.lifeState === LIFE.DORMANT) dormant++;
    }
    const n = Math.max(1, save.creatures.length);
    const mini: MiniSave = {
      v: SAVE_VERSION,
      seed: save.seed,
      createdAt: save.createdAt,
      savedAt: save.savedAt,
      simTick: save.simTick,
      simTimeSec: save.simTimeSec,
      pop: save.creatures.length,
      dormant,
      needAvg: [(h / n) | 0, (c / n) | 0, (f / n) | 0, (r / n) | 0],
      onboarding: save.onboarding,
    };
    localStorage.setItem(KEY, JSON.stringify(mini));
  } catch {
    // quota/private-mode failures are non-fatal
  }
}

/** Rebuild an approximate SaveV1 from the mini snapshot ("the Throng remembers you"). */
export function expandMini(): SaveV1 | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const mini = JSON.parse(raw) as MiniSave;
    if (mini.v !== SAVE_VERSION || typeof mini.seed !== 'string') return null;
    const creatures = [];
    // deterministic scatter from the save seed — no Math.random
    let s = 0;
    for (let i = 0; i < mini.seed.length; i++) s = (s * 31 + mini.seed.charCodeAt(i)) | 0;
    const jitter = (i: number, salt: number): number => {
      let x = Math.imul(i + 1, 0x9e3779b9) ^ Math.imul(salt, 0x85ebca6b) ^ s;
      x = Math.imul(x ^ (x >>> 15), 0x27d4eb2d);
      return ((x ^ (x >>> 13)) >>> 0) / 4294967296;
    };
    for (let i = 0; i < mini.pop; i++) {
      const a = jitter(i, 1) * Math.PI * 2;
      const rr = Math.sqrt(jitter(i, 2)) * 18;
      const dormant = i < mini.dormant;
      creatures.push({
        x: Math.sin(a) * rr,
        z: Math.cos(a) * rr,
        seed: (jitter(i, 3) * 256) | 0,
        phase: (jitter(i, 4) * 256) | 0,
        scaleQ: 200,
        hunger: mini.needAvg[0],
        clean: mini.needAvg[1],
        fun: mini.needAvg[2],
        rest: mini.needAvg[3],
        lifeState: dormant ? LIFE.DORMANT : LIFE.CONTENT,
        lowMoodSec: 0,
        ageSec: 3600,
        splitCharge: 0,
        splitCooldown: 300,
        circadian: (jitter(i, 5) - 0.5) * 0.3,
        speedMul: 0.85 + jitter(i, 6) * 0.3,
      });
    }
    return {
      version: SAVE_VERSION,
      seed: mini.seed,
      createdAt: mini.createdAt,
      savedAt: mini.savedAt,
      simTick: mini.simTick,
      simTimeSec: mini.simTimeSec,
      rng: {
        steer: [s | 1, s ^ 2, s ^ 3, s ^ 4],
        spawn: [s ^ 5, s | 2, s ^ 7, s ^ 8],
        needs: [s ^ 9, s ^ 10, s | 4, s ^ 12],
        mitosis: [s ^ 13, s ^ 14, s ^ 15, s | 8],
      },
      creatures,
      items: [],
      onboarding: mini.onboarding,
    };
  } catch {
    return null;
  }
}
